import Phaser from 'phaser';
import type { ItemTemplateListMessage } from '@shared/types/messages';
import { GAME_FONT_FAMILY } from '@shared/constants';

/**
 * Helper para agregar la fuente del juego y resolución alta a un estilo de texto.
 * La resolución alta hace que los textos se vean nítidos incluso con pixelArt: true.
 */
function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return {
    fontFamily: GAME_FONT_FAMILY,
    resolution: 2, // Resolución alta para textos nítidos
    ...style,
  } as T;
}

type ItemPanelCallbacks = {
  onMove(x: number, y: number): void;
  onSpawnItem(itemId: string): void;
};

type ItemPanelOptions = {
  initialX: number;
  initialY: number;
};

type ItemPanelElements = {
  bg: Phaser.GameObjects.Rectangle;
  title: Phaser.GameObjects.Text;
  listBg: Phaser.GameObjects.Rectangle;
  list: Phaser.GameObjects.Text[];
  nameText: Phaser.GameObjects.Text;
  detailText: Phaser.GameObjects.Text;
  spawnButton: Phaser.GameObjects.Text;
  closeButton: Phaser.GameObjects.Text;
  panelX: number;
  panelY: number;
  panelWidth: number;
  panelHeight: number;
  colPadding: number;
  listColWidth: number;
  detailColWidth: number;
};

export class ItemPanel {
  private readonly scene: Phaser.Scene;
  private readonly callbacks: ItemPanelCallbacks;
  private panel: ItemPanelElements | null = null;
  private isOpen = false;
  private isDragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;
  private templates: ItemTemplateListMessage['templates'] = [];
  private selectedIndex: number = -1;

  constructor(scene: Phaser.Scene, callbacks: ItemPanelCallbacks, options: ItemPanelOptions) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.buildPanel(options.initialX, options.initialY);
    this.setVisible(false);
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    this.setVisible(this.isOpen);
  }

  open(): void {
    this.isOpen = true;
    this.setVisible(true);
  }

  close(): void {
    this.isOpen = false;
    this.setVisible(false);
  }

  isOpenPanel(): boolean {
    return this.isOpen;
  }

  setTemplates(templates: ItemTemplateListMessage['templates']): void {
    this.templates = templates;
    if (this.templates.length > 0 && this.selectedIndex < 0) {
      this.selectedIndex = 0;
    } else if (this.selectedIndex >= this.templates.length) {
      this.selectedIndex = Math.max(0, this.templates.length - 1);
    }
    this.refreshContents();
  }

  getTemplates(): ItemTemplateListMessage['templates'] {
    return this.templates;
  }

  handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.isDragging || !this.panel) return;
    const newX = pointer.x - this.dragOffsetX;
    const newY = pointer.y - this.dragOffsetY;
    this.move(newX, newY);
    this.callbacks.onMove(newX, newY);
  }

  handlePointerUp(pointer: Phaser.Input.Pointer): void {
    if (this.isDragging) {
      this.isDragging = false;
      pointer.event.stopPropagation();
    }
  }

  isDraggingPanel(): boolean {
    return this.isDragging;
  }

  isPointerOver(pointer: Phaser.Input.Pointer): boolean {
    if (!this.panel || !this.isOpen) return false;
    const { panelX, panelY, panelWidth, panelHeight } = this.panel;
    const left = panelX - panelWidth / 2;
    const right = panelX + panelWidth / 2;
    const top = panelY - panelHeight / 2;
    const bottom = panelY + panelHeight / 2;
    return pointer.x >= left && pointer.x <= right && pointer.y >= top && pointer.y <= bottom;
  }

  private buildPanel(initialX: number, initialY: number): void {
    const panelWidth = 520;
    const panelHeight = 260;
    const panelX = initialX;
    const panelY = initialY;

    const bg = this.scene.add
      .rectangle(panelX, panelY, panelWidth, panelHeight, 0x111111, 0.9)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x666666)
      .setDepth(1100)
      .setScrollFactor(0)
      // Fondo interactivo: absorbe hover/click de objetos del mundo debajo
      // (Phaser topOnly) para que la ventana no deje pasar clicks ni cambie el cursor.
      .setInteractive()
      .setVisible(false);

    const colPadding = 12;
    const listColWidth = panelWidth * 0.4;
    const detailColWidth = panelWidth * 0.6 - colPadding * 3;

    const baseX = panelX - panelWidth / 2;
    const baseY = panelY - panelHeight / 2;
    const leftStartX = baseX + colPadding;
    const rightStartX = leftStartX + listColWidth + colPadding;

    const listBg = this.scene.add
      .rectangle(
        leftStartX + listColWidth / 2,
        panelY,
        listColWidth,
        panelHeight - colPadding * 2,
        0x222222,
        0.8
      )
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x555555)
      .setDepth(1100)
      .setScrollFactor(0)
      .setVisible(false);

    const title = this.scene.add
      .text(panelX, baseY + 16, 'Crear Items', withGameFont({
        fontSize: '16px',
        color: '#ffffff',
      }))
      .setOrigin(0.5, 0.5)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });

    title.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.isDragging = true;
      this.dragOffsetX = pointer.x - bg.x;
      this.dragOffsetY = pointer.y - bg.y;
      pointer.event.stopPropagation();
    });

    const listStartY = baseY + 36;
    const rowHeight = 18;
    const list: Phaser.GameObjects.Text[] = [];

    for (let i = 0; i < 14; i += 1) {
      const y = listStartY + i * rowHeight;
      const txt = this.scene.add
        .text(leftStartX + 6, y, '', withGameFont({
          fontSize: '12px',
          color: '#ffffff',
        }))
        .setOrigin(0, 0)
        .setDepth(1101)
        .setScrollFactor(0)
        .setVisible(false)
        .setInteractive({ useHandCursor: true });

      const index = i;
      txt.on('pointerup', (pointer: Phaser.Input.Pointer) => {
        this.handleListClick(index);
        pointer.event.stopPropagation();
      });

      list.push(txt);
    }

    const nameText = this.scene.add
      .text(rightStartX, listStartY, '', withGameFont({
        fontSize: '14px',
        color: '#ffffff',
        fontStyle: 'bold',
        wordWrap: { width: detailColWidth - colPadding * 2 },
      }))
      .setOrigin(0, 0)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);

    const detailText = this.scene.add
      .text(rightStartX, listStartY + 22, '', withGameFont({
        fontSize: '12px',
        color: '#dddddd',
        wordWrap: { width: detailColWidth - colPadding * 2 },
      }))
      .setOrigin(0, 0)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);

    const spawnButton = this.scene.add
      .text(rightStartX, panelY + panelHeight / 2 - 32, '[ Tirar en este tile ]', withGameFont({
        fontSize: '14px',
        color: '#ffffaa',
        backgroundColor: '#333333',
      }))
      .setOrigin(0, 0.5)
      .setPadding(8, 4, 8, 4)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });

    spawnButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (this.selectedIndex >= 0 && this.selectedIndex < this.templates.length) {
        const selected = this.templates[this.selectedIndex];
        if (selected) {
          this.callbacks.onSpawnItem(selected.id);
        }
      }
      pointer.event.stopPropagation();
    });

    const closeButton = this.scene.add
      .text(panelX + panelWidth / 2 - 16, baseY + 8, '✕', withGameFont({
        fontSize: '14px',
        color: '#ffffff',
        backgroundColor: '#555555',
      }))
      .setOrigin(1, 0)
      .setPadding(4, 2, 4, 2)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });

    closeButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.close();
      pointer.event.stopPropagation();
    });

    this.panel = {
      bg,
      title,
      listBg,
      list,
      nameText,
      detailText,
      spawnButton,
      closeButton,
      panelX,
      panelY,
      panelWidth,
      panelHeight,
      colPadding,
      listColWidth,
      detailColWidth,
    };
  }

  private handleListClick(index: number): void {
    if (index < 0 || index >= this.templates.length) return;
    this.selectedIndex = index;
    this.refreshContents();
  }

  private refreshContents(): void {
    if (!this.panel) return;

    for (let i = 0; i < this.panel.list.length; i += 1) {
      const txt = this.panel.list[i];
      const tmpl = this.templates[i];

      if (tmpl) {
        txt.setText(`${tmpl.id} - ${tmpl.name}`);
        if (i === this.selectedIndex) {
          txt.setColor('#ffff00');
        } else {
          txt.setColor('#ffffff');
        }
      } else {
        txt.setText('');
      }
    }

    if (this.templates.length === 0 || this.selectedIndex < 0) {
      this.panel.nameText.setText('');
      this.panel.detailText.setText('No hay templates de items disponibles.');
      return;
    }

    const selected = this.templates[this.selectedIndex];
    if (!selected) {
      this.panel.nameText.setText('');
      this.panel.detailText.setText('No hay templates de items disponibles.');
      return;
    }

    this.panel.nameText.setText(selected.name);
    this.panel.detailText.setText(`ID: ${selected.id}\nIcono: ${selected.icon}`);
  }

  private setVisible(visible: boolean): void {
    if (!this.panel) return;
    const panel = this.panel;
    panel.bg.setVisible(visible).setActive(visible);
    panel.title.setVisible(visible).setActive(visible);
    panel.listBg.setVisible(visible).setActive(visible);
    panel.list.forEach((txt) => txt.setVisible(visible).setActive(visible));
    panel.nameText.setVisible(visible).setActive(visible);
    panel.detailText.setVisible(visible).setActive(visible);
    panel.spawnButton.setVisible(visible).setActive(visible);
    panel.closeButton.setVisible(visible).setActive(visible);
    if (visible) {
      this.refreshContents();
    }
  }

  private move(newX: number, newY: number): void {
    if (!this.panel) return;
    const panel = this.panel;
    panel.panelX = newX;
    panel.panelY = newY;

    const { panelWidth, panelHeight, colPadding, listColWidth } = panel;
    const baseX = newX - panelWidth / 2;
    const baseY = newY - panelHeight / 2;
    const leftStartX = baseX + colPadding;
    const rightStartX = leftStartX + listColWidth + colPadding;
    const listStartY = baseY + 36;
    const rowHeight = 18;

    panel.bg.setPosition(newX, newY);
    panel.title.setPosition(newX, baseY + 16);
    panel.listBg.setPosition(leftStartX + listColWidth / 2, newY);

    panel.list.forEach((txt, i) => {
      txt.setPosition(leftStartX + 6, listStartY + i * rowHeight);
    });

    panel.nameText.setPosition(rightStartX, listStartY);
    panel.detailText.setPosition(rightStartX, listStartY + 22);
    panel.spawnButton.setPosition(rightStartX, newY + panelHeight / 2 - 32);
    panel.closeButton.setPosition(newX + panelWidth / 2 - 16, baseY + 8);
  }
}

